import type { ZoomStateInput } from './types/zoom-state-input'
import type { FitToSpreadShellAction } from './types/fit-to-spread-shell-action'
/**
 * Core editor zoom math shared by the home shell (toolbar, keyboard, zoom control).
 * Canvas/trackpad pinch lives in `center-canvas/hooks/use-ctrl-wheel-zoom.ts` (Safari:
 * `use-safari-pinch-zoom.ts`).
 */

export const EDITOR_ZOOM_MIN = 0.1
export const EDITOR_ZOOM_MAX = 8
/** Additive step on zoom factor (0.1 = +10 percentage points, e.g. 1.0 → 1.1). */
export const EDITOR_ZOOM_ADDITIVE_STEP = 0.1

/**
 * Next zoom level for toolbar / keyboard “zoom in” when the canvas does not handle it
 * (see `CenterCanvasHandle.zoomInFromToolbar`).
 */
export function computeNextZoomIn({ zoom, effectiveCanvasZoom }: ZoomStateInput): number {
  const current = zoom === 0 ? effectiveCanvasZoom : zoom

  if (+current.toFixed(3) >= EDITOR_ZOOM_MAX) {
    return zoom
  }

  const stepped = +(current + EDITOR_ZOOM_ADDITIVE_STEP).toFixed(3)
  const next = Math.min(EDITOR_ZOOM_MAX, stepped)

  return +next.toFixed(3)
}

/**
 * Next zoom level for “zoom out” when the canvas does not handle it
 * (`CenterCanvasHandle.zoomOutFromToolbar`). Exiting fit uses `effectiveCanvasZoom` as the base.
 */
export function computeNextZoomOut({ zoom, effectiveCanvasZoom }: ZoomStateInput): number {
  const current = zoom === 0 ? effectiveCanvasZoom : zoom
  const stepped = +(current - EDITOR_ZOOM_ADDITIVE_STEP).toFixed(3)
  const next = Math.max(EDITOR_ZOOM_MIN, stepped)

  return +next.toFixed(3)
}

/** Navbar / zoom dropdown explicit percent commit. */
export function commitZoomPercent(percent: number): number {
  const z = Math.max(EDITOR_ZOOM_MIN, Math.min(EDITOR_ZOOM_MAX, percent / 100))

  return +z.toFixed(3)
}

export function isFitToSpreadAllowed(
  hasDocumentForFit: boolean,
  fitToSpreadViewportOk: boolean
): boolean {
  return hasDocumentForFit && fitToSpreadViewportOk
}

/**
 * What the shell should do for “fit to spread” (toolbar / Ctrl+1), excluding canvas ref calls.
 */
export function resolveFitToSpreadShellAction(
  zoom: number,
  hasDocumentForFit: boolean,
  fitToSpreadViewportOk: boolean
): FitToSpreadShellAction {
  if (!isFitToSpreadAllowed(hasDocumentForFit, fitToSpreadViewportOk)) {
    return 'noop'
  }

  if (zoom === 0) {
    return 'refit'
  }

  return 'enter'
}
